// Data-access visibility panel — answers "what can the analyst see?" with a
// live, introspected inventory: Postgres tables, Sage MySQL tables, and the
// Knowledge Bank allowlist. Renders the generic sections → groups → items
// shape, so new backend data sources appear here without frontend changes.

import { useEffect, useMemo, useState } from 'react';
import {
  filterReportSections,
  getDataAccessReport,
  sectionItemCount,
  type DataAccessGroup,
  type DataAccessItem,
  type DataAccessReport,
  type DataAccessSection,
} from '../../services/agentConsoleDataAccess';
import styles from './DataAccessPanel.module.css';

function ItemRow({ item }: { item: DataAccessItem }) {
  const chips = Object.entries(item.meta).filter(
    // server_id is internal plumbing; "table" is the overwhelming default —
    // only chips that carry signal get rendered.
    ([key, value]) => key !== 'server_id' && value !== 'table',
  );
  return (
    <li className={styles.item}>
      <div className={styles.itemHead}>
        <code className={styles.itemName}>{item.name}</code>
        {chips.map(([key, value]) => (
          <span key={key} className={styles.chip}>
            {key === 'type' ? value : `${key} ${value}`}
          </span>
        ))}
      </div>
      {item.description && (
        <p className={styles.itemDescription}>{item.description}</p>
      )}
    </li>
  );
}

function GroupBlock({
  group,
  forceOpen,
}: {
  group: DataAccessGroup;
  forceOpen: boolean;
}) {
  const [open, setOpen] = useState(false);
  const expanded = forceOpen || open;
  return (
    <div className={styles.group}>
      <button
        type="button"
        className={styles.groupHeader}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={expanded}
      >
        <span className={`${styles.caret} ${expanded ? styles.caretOpen : ''}`}>
          ▸
        </span>
        <code className={styles.groupName}>{group.name}</code>
        <span className={styles.count}>{group.items.length}</span>
      </button>
      {group.description && (
        <p className={styles.groupDescription}>{group.description}</p>
      )}
      {expanded && (
        <ul className={styles.itemList}>
          {group.items.map((item) => (
            <ItemRow key={item.name} item={item} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SectionBlock({
  section,
  filtering,
}: {
  section: DataAccessSection;
  filtering: boolean;
}) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h4>{section.title}</h4>
        {section.status === 'ok' ? (
          <span className={styles.count}>{sectionItemCount(section)}</span>
        ) : (
          <span className={styles.errorBadge}>unavailable</span>
        )}
      </div>
      <p className={styles.sectionSummary}>{section.summary}</p>
      {section.status === 'error' ? (
        <p className={styles.sectionError}>{section.error}</p>
      ) : (
        section.groups.map((group) => (
          <GroupBlock
            key={group.name}
            group={group}
            // A single small group (e.g. the KB list) reads better open; big
            // multi-group sources start collapsed. An active filter opens
            // everything so matches are visible.
            forceOpen={filtering || group.items.length <= 12}
          />
        ))
      )}
    </section>
  );
}

/**
 * Embeddable live data-access report — the developer-facing table/bank
 * inventory. Lives as a section inside the Inspect modal (the plain-language
 * "Can access" summary is the non-technical view; this is the drill-down).
 * Fetches once on mount; the filter narrows tables/banks/descriptions.
 */
export function DataAccessReportView() {
  const [report, setReport] = useState<DataAccessReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (report || loading) return;
    setLoading(true);
    setError(null);
    getDataAccessReport()
      .then(setReport)
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Failed to load data access'),
      )
      .finally(() => setLoading(false));
    // Mount-once fetch: report/loading guard prevents a refetch loop.
  }, [report, loading]);

  const visibleSections = useMemo(
    () => (report ? filterReportSections(report.sections, query) : []),
    [report, query],
  );

  return (
    <div className={styles.reportView}>
      <p className={styles.panelIntro}>
        The live inventory of every table and bank the agent&apos;s read-only
        credentials can see right now — the developer drill-down behind the
        plain-language access summary. New tables and banks appear automatically.
      </p>
      <input
        type="search"
        className={styles.filterInput}
        placeholder="Filter tables, banks, descriptions…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {loading && <p className={styles.statusLine}>Introspecting…</p>}
      {error && (
        <p className={`${styles.statusLine} ${styles.statusError}`}>{error}</p>
      )}
      {report && visibleSections.length === 0 && !loading && (
        <p className={styles.statusLine}>No matches for “{query.trim()}”.</p>
      )}
      {visibleSections.map((section) => (
        <SectionBlock
          key={section.id}
          section={section}
          filtering={query.trim().length > 0}
        />
      ))}
    </div>
  );
}
